import '../styles/address-search-input.css'
import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'

import * as olProj from 'ol/proj';

import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';

import debounce from 'lodash.debounce';

import { utahExtent } from '../constants';

import LoadingComponent from './loading';

const zoomOnSearch = 16;

class AddressSearchInputComponent extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            addressInput: '',
            addressList: [],
        }

        this.searchForSuggestionsDebounced = debounce(this.searchForSuggestions, 400, { leading: true, trailing: true });
    }

    /*
        This function takes a suggestion entry and returns the location of the suggestion
        Note that the only binding is the display value of the suggestion so it is possible to submit an invalid suggestion if the display is changed
    */
    getLocationForSuggestion = async (suggestion) => {

        // Set our state such that our input matches the suggestion and clear the list of suggestions
        this.setState({
            addressInput: suggestion.display,
            addressList: [],
        });

        // Get the center of the map to use as a bias
        let centerOfMap = window.mapObj.getView().getCenter();
        let lonLat = olProj.transform(centerOfMap, 'EPSG:3857', 'EPSG:4326');


        // Clamp our location to the utah extent
        lonLat = this.clampLonLatToExtent(lonLat, utahExtent);

        // Build the URL we will use to get the location
        // I dont think there will be an issue right now but in the future the parameters may need to be encoded into the body instead of the URL
        let locationUrl = `/api/geocode/locate?text=${encodeURIComponent(suggestion.display)}&lon=${lonLat[0]}&lat=${lonLat[1]}`;

        // Wait for the response and convert it to a JSON object
        let response = await (await fetch(locationUrl)).json();

        // The response should be a list of locations
        // We only care about the first location
        if (response && response.length > 0) {
            let location = response[0];

            let geom = null;

            // Try to convert the location to a geometry the map can use
            // I have not seen any polygon responses but it is possible
            if (location.Place.Geometry && location.Place.Geometry.Point) {
                geom = new Point(olProj.fromLonLat(location.Place.Geometry.Point));
            }
            else if (location.Place.Geometry && location.Place.Geometry.Polygon) {
                geom = new Polygon(location.Geometry.Polygon);
            }
            else {
                // I am unsure how to handle this case so for now it will just log it
                console.error("No geometry found for suggestion", location);
            }

            // Tell the map to fit the geometry in the view
            // In the future we should consider passing in the map object into this component somehow so we are not dependent on the global map object
            window.mapObj.getView().fit(geom, {
                duration: 1000,
                maxZoom: zoomOnSearch
            });

            // If we have an onLocationFound callback, call it
            if (this.props.onLocationFound) {
                this.props.onLocationFound(suggestion);
            }
        }

    }


    /*
        Sometimes we need to update our internal state when our props change
    */
    componentDidUpdate = (prevProps, prevState) => {
        // Adjust our state to match the props if it has changed
        if (this.props.startAddress && this.props.startAddress !== prevProps.startAddress) {
            this.setState({ addressInput: this.props.startAddress });
        }
    }

    /*
        This function takes a location and clamps it to the provided extent
        Consider using ol.extent.clamp instead
        Consider moving this to a utility file
    */
    clampLonLatToExtent = (lonLat, extent) => {
        if (lonLat[0] < extent[0]) {
            lonLat[0] = extent[0];
        }
        if (lonLat[0] > extent[2]) {
            lonLat[0] = extent[2];
        }
        if (lonLat[1] < extent[1]) {
            lonLat[1] = extent[1];
        }
        if (lonLat[1] > extent[3]) {
            lonLat[1] = extent[3];
        }

        return lonLat;
    }


    /*
        This function handles the address search input change
        It will search for suggestions and update the state
    */
    handleAddressInputChange = async (event) => {
        let newInputValue = event.target.value;
        this.setState({
            addressInput: newInputValue,
        });

        this.searchForSuggestionsDebounced(newInputValue);

        // If we have an onAddressInputChange callback, call it
        if (this.props.onAddressInputChange) {
            this.props.onAddressInputChange(newInputValue);
        }
    }

    /*
        This function gets the suggestions for the provided input
    */
    getAndPopulateSuggestions = async (newInputValue) => {
        // Get the center of the map to use as a bias. I think theres a toLatLon function somewhere
        let centerOfMap = window.mapObj.getView().getCenter();
        let lonLat = olProj.transform(centerOfMap, 'EPSG:3857', 'EPSG:4326');

        lonLat = this.clampLonLatToExtent(lonLat, utahExtent);

        // Build the URL we will use to get the suggestions
        let suggestUrl = `/api/geocode/suggest?text=${encodeURIComponent(newInputValue)}&lon=${lonLat[0]}&lat=${lonLat[1]}`;

        // Set the state to searching for suggestions
        this.setState({ gettingSuggestions: true });

        // Wait for the response and convert it to a JSON object
        let response = await (await fetch(suggestUrl)).json();

        // Set the state to not searching for suggestions
        this.setState({ gettingSuggestions: false });

        // The response should be a list of suggestions
        // This will convert them to a list of objects that the autocomplete component can use
        if (response.length > 0) {
            let opts = response.map(c => {
                return {
                    display: c.Text,
                    isSuggestion: true,
                }
            });

            this.setState({
                addressList: opts,
            });
        }
    }

    /*
        This function is a wrapper for the getAndPopulateSuggestions function
        It is debounced so that we dont make too many requests
        Additionally itt has some logic to not try to sugegst when the input is empty or too short
    */
    searchForSuggestions = async (newInputValue) => {
        if (!newInputValue) {
            this.setState({
                addressList: [],
            });
            return;
        }

        if (newInputValue.length < 5) {
            this.setState({
                addressInput: newInputValue,
                addressList: [],
            });
        }
        else {
            this.getAndPopulateSuggestions(newInputValue);
        }
    }

    clearInput = () => {
        this.setState({ addressInput: '', addressList: [], gettingSuggestions: false });
    }

    /*
        This function assists with the logic for rendering suggestions
    */
    renderSuggestions = () => {
        // If we have suggestions and are not searching for them return the suggestions
        if (this.state.addressList.length > 0 && this.state.gettingSuggestions === false) {
            return (
                <div className="suggest">
                    {this.state.addressList.length} suggestions
                    <hr />
                    {
                        this.state.addressList.map((item, index) => {
                            return (<button className='suggestion' onClick={() => { this.getLocationForSuggestion(item); }} ><div className="search-icon-container nobackground" ><FontAwesomeIcon icon={faSearch} className="search-icon" color={"#AA4000"} /></div>{item.display}</button>);
                        })
                    }
                </div>
            );
        } else if (this.state.gettingSuggestions) { // If we are searching for suggestions return a spinner
            return (
                <div className="suggest">
                    <LoadingComponent />
                </div>
            );
        }
        else if (this.state.addressInput.length > 0 && this.state.addressInput.length < 5) { // If the input is too short return a message
            return (
                <div className="suggest suggest-help">
                    Enter at least 5 characters to get suggestions
                </div>
            );
        }
    }

    render = () => {
        return (
            <div className='address-search-container'>
                <div className="address-search-input">
                    <div className="search-icon-container" ><FontAwesomeIcon icon={faSearch} className="search-icon" color={"#AA4000"} /></div>
                    <input type="text" value={this.state.addressInput} onChange={this.handleAddressInputChange} placeholder="Enter an address or place name" />
                    <button className={this.state.addressInput.length > 0 ? 'clear-button' : 'hidden'} onClick={this.clearInput}><FontAwesomeIcon icon={faTimes} className="clear-icon" color={"#AA4000"} /></button>
                    <button type="submit" onClick={() => { this.getLocationForSuggestion({ display: this.state.addressInput }); }}>Let's Go!</button>
                </div>
                {this.renderSuggestions()}
            </div>
        );
    }
}

export default AddressSearchInputComponent;